import React, { useEffect, useState } from 'react';
import { Box, Text, Container } from '@mantine/core';

interface LoadingScreenProps {
  onComplete: () => void;
  duration?: number;
}

interface Package {
  name: string;
  progress: number;
  completed: boolean;
  color: string;
}

export const LoadingScreen: React.FC<LoadingScreenProps> = ({
  onComplete,
  duration = 1500
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const [packages, setPackages] = useState<Package[]>([
    { name: 'react', progress: 0, completed: false, color: '#61DAFB' },
    { name: '@mantine/core', progress: 0, completed: false, color: '#339AF0' },
    { name: 'three', progress: 0, completed: false, color: '#FF6B6B' },
    { name: '@tabler/icons-react', progress: 0, completed: false, color: '#51CF66' },
    { name: 'typescript', progress: 0, completed: false, color: '#3178C6' },
    { name: 'portfolio-components', progress: 0, completed: false, color: '#9775FA' }
  ]);

  const steps = [
    "$ npm install",
    "npm WARN using --force",
    "added 1337 packages in 2.34s"
  ];

  useEffect(() => {
    // Disable scrolling when loading screen is active
    document.body.style.overflow = 'hidden';
    
    let timeoutId: NodeJS.Timeout;
    
    const startAnimation = async () => {
      // Step 1: Show npm install command
      setCurrentStep(1);
      
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Step 2: Show warning
      setCurrentStep(2);
      
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Step 3: Start package installation with progress bars
      const packageInstallDuration = 1000; // 1 second for all packages
      const intervalDuration = 50; // Update every 50ms
      const totalIntervals = packageInstallDuration / intervalDuration;
      
      let currentInterval = 0;
      
      const progressInterval = setInterval(() => {
        currentInterval++;
        const overallProgress = currentInterval / totalIntervals;
        
        setPackages(prevPackages => 
          prevPackages.map((pkg, index) => {
            // Stagger the start times for each package
            const startDelay = index * 0.15;
            const adjustedProgress = Math.max(0, overallProgress - startDelay) / (1 - startDelay);
            const clampedProgress = Math.min(100, adjustedProgress * 100);
            
            return {
              ...pkg,
              progress: clampedProgress,
              completed: clampedProgress >= 100
            };
          })
        );
        
        if (currentInterval >= totalIntervals) {
          clearInterval(progressInterval);
          
          // Step 4: Show completion message
          setTimeout(() => {
            setCurrentStep(3);
            
            // Fade out after showing completion
            setTimeout(() => {
              setIsVisible(false);
              setTimeout(onComplete, 200);
            }, 200);
          }, 100);
        }
      }, intervalDuration);
    };

    timeoutId = setTimeout(startAnimation, 100);
    
    return () => {
      clearTimeout(timeoutId);
      // Re-enable scrolling when component unmounts
      document.body.style.overflow = 'unset';
    };
  }, [onComplete]);

  if (!isVisible) return null;

  const renderProgressBar = (pkg: Package) => {
    const filledBlocks = Math.floor((pkg.progress / 100) * 20);
    const emptyBlocks = 20 - filledBlocks;
    
    return (
      <Text
        key={pkg.name}
        style={{
          color: '#FFFFFF',
          fontSize: '0.9rem',
          fontFamily: 'inherit',
          marginBottom: '0.3rem',
          lineHeight: 1.2
        }}
      >
        <span style={{ color: '#666', minWidth: '140px', display: 'inline-block' }}>
          {pkg.name}
        </span>
        <span style={{ marginLeft: '10px' }}>
          {'█'.repeat(filledBlocks)}
          <span style={{ color: pkg.color }}>
            {'█'.repeat(Math.min(1, Math.max(0, filledBlocks)))}
          </span>
          {'░'.repeat(emptyBlocks)}
        </span>
        <span style={{ marginLeft: '10px', color: '#888', fontSize: '0.8rem' }}>
          {pkg.progress.toFixed(0)}%
        </span>
        {pkg.completed && (
          <span style={{ color: '#51CF66', marginLeft: '5px' }}>✓</span>
        )}
      </Text>
    );
  };

  return (
    <Box
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: '#0a0a0a',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        opacity: isVisible ? 1 : 0,
        transition: 'opacity 0.3s ease-out',
        fontFamily: '"Fira Code", "JetBrains Mono", "Courier New", monospace'
      }}
    >
      <Container size="lg" style={{ width: '100%' }}>
        <Box
          style={{
            padding: '2rem',
            maxWidth: '700px'
          }}
        >
          {/* Command line */}
          {currentStep >= 1 && (
            <Text
              style={{
                color: '#51CF66',
                fontSize: '1.1rem',
                fontFamily: 'inherit',
                marginBottom: '1rem'
              }}
            >
              {steps[0]}
            </Text>
          )}
          
          {/* Warning message */}
          {currentStep >= 2 && (
            <Text
              style={{
                color: '#FFD93D',
                fontSize: '0.9rem',
                fontFamily: 'inherit',
                marginBottom: '1rem'
              }}
            >
              {steps[1]}
            </Text>
          )}
          
          {/* Package installation progress */}
          {currentStep >= 2 && (
            <Box style={{ marginBottom: '1rem' }}>
              {packages.map(renderProgressBar)}
            </Box>
          )}
          
          {/* Completion message */}
          {currentStep >= 3 && (
            <Text
              style={{
                color: '#51CF66',
                fontSize: '1rem',
                fontFamily: 'inherit'
              }}
            >
              {steps[2]}
            </Text>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default LoadingScreen; 